import type { KeyboardScope } from "~/keyboard/scope";

import { ApplicationEnum } from "~/@types/enums";
import dockShellRoutes from "~/components/dock/dockShellRoutes";
import { ORDERS_DOCK_PAGE_ID } from "~/helpers/panelKey";

export type NavigationChordId =
	| "nav.availability"
	| "nav.benchmark"
	| "nav.count"
	| "nav.engage"
	| "nav.fulfill"
	| "nav.intelligence"
	| "nav.material"
	| "nav.orders"
	| "nav.produce"
	| "nav.restock"
	| "nav.settings"
	| "nav.verify";

interface NavigationChordDef {
	id: NavigationChordId;
	label: string;
	labelKey?: string;
	letter: string;
	module: ApplicationEnum;
	page?: string;
}

const navigationChordDefs: NavigationChordDef[] = [
	{ id: "nav.orders", label: "Orders", letter: "o", module: ApplicationEnum.FULFILL, page: ORDERS_DOCK_PAGE_ID },
	{ id: "nav.fulfill", label: "Fulfill", letter: "f", module: ApplicationEnum.FULFILL },
	{ id: "nav.produce", label: "Produce", letter: "p", module: ApplicationEnum.PRODUCE },
	{ id: "nav.restock", label: "Restock", letter: "r", module: ApplicationEnum.RESTOCK },
	{ id: "nav.availability", label: "Availability", letter: "a", module: ApplicationEnum.AVAILABILITY },
	{ id: "nav.material", label: "Material", letter: "m", module: ApplicationEnum.MATERIAL },
	{ id: "nav.settings", label: "Settings", letter: "s", module: ApplicationEnum.ADMIN },
	{ id: "nav.verify", label: "Verify", letter: "v", module: ApplicationEnum.VERIFY },
	{ id: "nav.count", label: "Count", letter: "c", module: ApplicationEnum.COUNT },
	{ id: "nav.engage", label: "Engage", letter: "e", module: ApplicationEnum.ENGAGE },
	{ id: "nav.intelligence", label: "Intelligence", letter: "i", module: ApplicationEnum.INTELLIGENCE },
	{
		id: "nav.benchmark",
		label: "Benchmark",
		labelKey: "nav.system.benchmark.label",
		letter: "b",
		module: ApplicationEnum.BENCHMARK
	}
];

const routePrefixForModule = (module: ApplicationEnum): string | undefined =>
	dockShellRoutes.find((route) => route.module === module)?.routePrefix;

export interface NavigationChord {
	binding: string;
	id: NavigationChordId;
	label: string;
	module: ApplicationEnum;
	path: string;
}

const toNavigationChord = (def: NavigationChordDef): NavigationChord | undefined => {
	const prefix = routePrefixForModule(def.module);
	if (!prefix) return undefined;
	return {
		binding: `g ${def.letter}`,
		id: def.id,
		label: def.label,
		module: def.module,
		path: def.page ? `${prefix}/${def.page}` : prefix
	};
};

const navigationChords: NavigationChord[] = navigationChordDefs
	.map(toNavigationChord)
	.filter((chord): chord is NavigationChord => chord !== undefined);

export const NAVIGATION_CHORD_SCOPE: KeyboardScope = "global";

export default navigationChords;
